import posthog from "posthog-js";
import { useEffect, useRef, useState } from "react";

import { Button } from "../../../components/ui/button";
import { isExtractionDone, isExtractionFailed, useDSPExtractionStatus } from "../../../services/create-resume/queries";
import { EXTRACTION_TIMEOUT_MS, hasExtractionTimedOut } from "./useResumeSetup";

interface ExtractionWaitingProps {
	dspId?: string;
	/** Epoch ms, from the draft. Reloading must not restart the wait. */
	startedAt: number;
	/** The single exit. Runs whether the extraction finished, failed, or ran out of time. */
	onDone: () => void;
}

type Outcome = "waiting" | "done" | "failed" | "timedOut";

function ExtractionWaiting({ dspId, startedAt, onDone }: ExtractionWaitingProps) {
	// Read once so a re-render mid-wait cannot reset the countdown.
	const [timedOut, setTimedOut] = useState(() => hasExtractionTimedOut(startedAt));
	const { data: dspInfo } = useDSPExtractionStatus(dspId, { enabled: !timedOut });

	const done = isExtractionDone(dspInfo);
	const failed = isExtractionFailed(dspInfo);

	let outcome: Outcome = "waiting";
	if (done) outcome = "done";
	else if (failed) outcome = "failed";
	else if (timedOut) outcome = "timedOut";

	// Nothing to poll — the row went in but we never got an id back. Extraction is still running
	// server-side; there is just no way to watch it from here.
	const unwatchable = !dspId;

	const reportedTimeout = useRef(false);

	useEffect(() => {
		if (timedOut) return;

		const remaining = EXTRACTION_TIMEOUT_MS - (Date.now() - startedAt);
		const timer = setTimeout(() => setTimedOut(true), Math.max(0, remaining));
		return () => clearTimeout(timer);
	}, [startedAt, timedOut]);

	useEffect(() => {
		if (outcome !== "timedOut" || reportedTimeout.current) return;
		reportedTimeout.current = true;
		posthog.capture("registration_resume_extraction_timed_out", {
			waited_seconds: Math.round((Date.now() - startedAt) / 1000),
		});
	}, [outcome, startedAt]);

	// A finished extraction needs no button. Hand over the profile as soon as it lands.
	useEffect(() => {
		if (outcome === "done") onDone();
	}, [onDone, outcome]);

	if (outcome === "waiting" && !unwatchable) {
		return (
			<div className="py-12">
				<div className="flex flex-col items-center text-center">
					<span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary-50">
						<span className="h-7 w-7 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
					</span>
					<h3 className="font-bold! text-[28px] leading-tight text-[#161616] font-plus-jakarta! mb-2">
						Reading your resume…
					</h3>
					<p className="text-[#5C5C5C] text-sm max-w-sm">
						We are pulling out your experience, skills and education. This usually takes under a minute.
					</p>
				</div>
			</div>
		);
	}

	const headline = outcome === "failed" ? "We could not read that resume" : "This is taking longer than usual";

	const body =
		outcome === "failed"
			? "Your resume is saved to your profile, but we could not pull the details out of it. You can fill them in yourself, or try a different file from your profile."
			: "We are still reading your resume. Open your profile and refresh it in a minute to see the result.";

	return (
		<div className="py-12">
			<div className="flex flex-col items-center text-center">
				<h3 className="font-bold! text-[28px] leading-tight text-[#161616] font-plus-jakarta! mb-2">{headline}</h3>
				<p className="text-[#5C5C5C] text-sm max-w-sm mb-8">{body}</p>
				<Button className="w-full" type="button" onClick={onDone}>
					Go to my profile
				</Button>
			</div>
		</div>
	);
}

export default ExtractionWaiting;
